import { createContext, useContext, ReactNode, useEffect } from 'react';
import { Context as AppBridgeReactContext } from '@shopify/app-bridge-react';
import { setAppBridgeInstance } from '../api/client';
import { purgeStaleAdminApiKey } from '../utils/adminKey';

interface AppBridgeContextType {
  app: any;
}

const AppBridgeContext = createContext<AppBridgeContextType>({ app: null });

export function AppBridgeProvider({ children }: { children: ReactNode }) {
  // Read the App Bridge instance directly from the react package's context.
  // (useAppBridge() throws when the <Provider> is absent, e.g. local dev
  // outside Shopify Admin; useContext returns null instead.)
  // NOTE: static import — a runtime `require()` is undefined in the Vite
  // browser bundle, which used to leave this permanently null.
  const app = useContext(AppBridgeReactContext);

  // Register the instance DURING RENDER, not only in an effect. React runs
  // child effects before parent effects, so a child provider's mount effect
  // (e.g. BillingProvider.fetchPlan) would otherwise fire before this parent's
  // effect set the global — sending the first authenticated request with no
  // session token → 401. Setting it here (idempotent module-global write) makes
  // App Bridge available before any descendant renders/effects run.
  if (app) {
    setAppBridgeInstance(app);
  }

  useEffect(() => {
    if (app) {
      setAppBridgeInstance(app);
      // Embedded → the session token is the only credential we want. Drop any
      // legacy admin key so it can't override the shop the backend resolves.
      purgeStaleAdminApiKey();
    }
  }, [app]);

  return (
    <AppBridgeContext.Provider value={{ app }}>
      {children}
    </AppBridgeContext.Provider>
  );
}

export function useAppBridgeContext() {
  return useContext(AppBridgeContext);
}
